import { commons, universal } from '@0xsequence/core';
import { defaults, migrator, version } from '@0xsequence/migration';
import { isRelayer, RpcRelayer } from '@0xsequence/relayer';
import { getDefaultConnectionInfo, encodeTypedDataDigest } from '@0xsequence/utils';
import { Wallet } from '@0xsequence/wallet';
import { ethers } from 'ethers';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

class Chain0Reader {
  async isDeployed(_wallet) {
    return false;
  }
  async implementation(_wallet) {
    return undefined;
  }
  async imageHash(_wallet) {
    return undefined;
  }
  async nonce(_wallet, _space) {
    return ethers.constants.Zero;
  }
  async isValidSignature(_wallet, _digest, _signature) {
    throw new Error('Method not supported.');
  }
}
class Account {
  constructor(options) {
    this.address = ethers.utils.getAddress(options.address);
    this.contexts = options.contexts;
    this.tracker = options.tracker;
    this.networks = options.networks;
    this.orchestrator = options.orchestrator;
    this.migrations = options.migrations || defaults.DefaultMigrations;
    this.migrator = new migrator.Migrator(options.tracker, this.migrations, this.contexts);
  }
  static async new(options) {
    var _options$migrations;
    const mig = new migrator.Migrator(options.tracker, (_options$migrations = options.migrations) != null ? _options$migrations : defaults.DefaultMigrations, options.contexts);
    const lastMigration = mig.lastMigration();
    const lastCoder = lastMigration.configCoder;
    const config = lastCoder.fromSimple(options.config);
    const imageHash = lastCoder.imageHashOf(config);
    const context = options.contexts[lastMigration.version];
    const address = commons.context.addressOf(context, imageHash);
    await options.tracker.saveCounterfactualWallet({
      config,
      context: Object.values(options.contexts)
    });
    return new Account({
      address,
      tracker: options.tracker,
      contexts: options.contexts,
      networks: options.networks,
      orchestrator: options.orchestrator,
      migrations: options.migrations
    });
  }
  getAddress() {
    return Promise.resolve(this.address);
  }
  get version() {
    return this.migrator.lastMigration().version;
  }
  get coders() {
    const lastMigration = this.migrator.lastMigration();
    return {
      signature: lastMigration.signatureCoder,
      config: lastMigration.configCoder
    };
  }
  network(chainId) {
    const tcid = ethers.BigNumber.from(chainId);
    const found = this.networks.find(n => tcid.eq(n.chainId));
    if (!found) throw new Error(`Network not found for chainId ${chainId}`);
    return found;
  }
  provider(chainId) {
    const found = this.network(chainId);
    if (!found.provider && !found.rpcUrl) throw new Error(`Provider not found for chainId ${chainId}`);
    return found.provider || new ethers.providers.StaticJsonRpcProvider(getDefaultConnectionInfo(found.rpcUrl), {
      name: '',
      chainId: ethers.BigNumber.from(chainId).toNumber()
    });
  }
  reader(chainId) {
    if (ethers.constants.Zero.eq(chainId)) return new Chain0Reader();

    // TODO: Networks should be able to provide a reader directly
    // and we should default to the on-chain reader
    return new commons.reader.OnChainReader(this.provider(chainId));
  }
  relayer(chainId) {
    const found = this.network(chainId);
    if (!found.relayer) throw new Error(`Relayer not found for chainId ${chainId}`);
    if (isRelayer(found.relayer)) return found.relayer;
    return new RpcRelayer(found.relayer);
  }
  setOrchestrator(orchestrator) {
    this.orchestrator = orchestrator;
  }
  contextFor(version) {
    const ctx = this.contexts[version];
    if (!ctx) throw new Error(`Context not found for version ${version}`);
    return ctx;
  }
  walletForStatus(chainId, status) {
    const coder = universal.coderFor(status.version);
    return this.walletFor(chainId, this.contextFor(status.version), status.config, coder);
  }
  walletFor(chainId, context, config, coders) {
    const isNetworkZero = ethers.constants.Zero.eq(chainId);
    return new Wallet({
      config,
      context,
      chainId,
      coders,
      relayer: isNetworkZero ? undefined : this.relayer(chainId),
      address: this.address,
      orchestrator: this.orchestrator,
      reader: this.reader(chainId)
    });
  }

  // Get the status of the account on a given network
  // this does the following process:
  // 1. Get the current on-chain status of the wallet (version + imageHash)
  // 2. Get any pending migrations that have been signed by the wallet
  // 3. Get any pending configuration updates that have been signed by the wallet
  // 4. Fetch reverse lookups for both on-chain and pending configurations
  async status(chainId, longestPath = false) {
    var _this = this;
    const isDeployedPromise = this.reader(chainId).isDeployed(this.address);
    const counterfactualImageHashPromise = this.tracker.imageHashOfCounterfactualWallet({
      wallet: this.address
    }).then(r => {
      if (!r) throw new Error(`Counterfactual imageHash not found for wallet ${this.address}`);
      return r;
    });
    const counterFactualVersionPromise = counterfactualImageHashPromise.then(r => {
      return version.counterfactualVersion(this.address, r.imageHash, Object.values(this.contexts));
    });
    const onChainVersionPromise = async function () {
      const isDeployed = await isDeployedPromise;
      if (!isDeployed) return counterFactualVersionPromise;
      const implementation = await _this.reader(chainId).implementation(_this.address);
      if (!implementation) throw new Error(`Implementation not found for wallet ${_this.address}`);
      const versions = Object.values(_this.contexts);
      for (let i = 0; i < versions.length; i++) {
        if (versions[i].mainModule === implementation || versions[i].mainModuleUpgradable === implementation) {
          return versions[i].version;
        }
      }
      throw new Error(`Version not found for implementation ${implementation}`);
    }();
    const onChainImageHashPromise = async function () {
      const deployedImageHash = await _this.reader(chainId).imageHash(_this.address);
      if (deployedImageHash) return deployedImageHash;
      const counterfactualImageHash = await counterfactualImageHashPromise;
      if (counterfactualImageHash) return counterfactualImageHash.imageHash;
      throw new Error(`On-chain imageHash not found for wallet ${_this.address}`);
    }();
    const onChainConfigPromise = async function () {
      const onChainImageHash = await onChainImageHashPromise;
      const onChainConfig = await _this.tracker.configOfImageHash({
        imageHash: onChainImageHash
      });
      if (onChainConfig) return onChainConfig;
      throw new Error(`On-chain config not found for imageHash ${onChainImageHash}`);
    }();
    const onChainVersion = await onChainVersionPromise;
    const onChainImageHash = await onChainImageHashPromise;
    let fromImageHash = onChainImageHash;
    let lastVersion = onChainVersion;
    let signedMigrations = [];
    if (onChainVersion !== this.version) {
      // We either need to use the presigned configuration updates, or we haven't performed
      // any updates yet, so we can only use the on-chain imageHash as-is
      const presignedMigrate = await this.migrator.getAllMigratePresignedTransaction({
        address: this.address,
        fromImageHash: onChainImageHash,
        fromVersion: onChainVersion,
        chainId
      });

      // The migrator returns the original version and imageHash
      // if no presigned migration is found, so no need to check here
      fromImageHash = presignedMigrate.lastImageHash;
      lastVersion = presignedMigrate.lastVersion;
      signedMigrations = presignedMigrate.signedMigrations;
    }
    const presigned = await this.tracker.loadPresignedConfiguration({
      wallet: this.address,
      fromImageHash: fromImageHash,
      longestPath
    });
    const imageHash = presigned && presigned.length > 0 ? presigned[presigned.length - 1].nextImageHash : fromImageHash;
    const config = await this.tracker.configOfImageHash({
      imageHash
    });
    if (!config) {
      throw new Error(`Config not found for imageHash ${imageHash}`);
    }
    const isDeployed = await isDeployedPromise;
    const counterfactualImageHash = await counterfactualImageHashPromise;
    const checkpoint = universal.coderFor(lastVersion).config.checkpointOf(config);
    return {
      original: _extends({}, counterfactualImageHash, {
        version: await counterFactualVersionPromise
      }),
      onChain: {
        imageHash: onChainImageHash,
        config: await onChainConfigPromise,
        version: onChainVersion,
        deployed: isDeployed
      },
      fullyMigrated: lastVersion === this.version,
      signedMigrations,
      version: lastVersion,
      presignedConfigurations: presigned,
      imageHash,
      config,
      checkpoint,
      canOnchainValidate: onChainVersion === this.version && isDeployed
    };
  }
  mustBeFullyMigrated(status) {
    if (!status.fullyMigrated) {
      throw new Error(`Wallet ${this.address} is not fully migrated`);
    }
  }
  async predecorateTransactions(txs, status, chainId) {
    // if onchain wallet config is not up to date
    // then we should append an extra transaction that updates it
    // to the latest "lazy" state
    if (status.onChain.imageHash !== status.imageHash) {
      const wallet = this.walletForStatus(chainId, status);
      const updateConfig = await wallet.buildUpdateConfigurationTransaction(status.config);
      return [Array.isArray(txs) ? txs : [txs], updateConfig.transactions].flat();
    }
    return txs;
  }
  decorateTransactions(bundle, status) {
    const bootstrapBundle = this.buildBootstrapTransactions(status, bundle.chainId);
    if (bootstrapBundle.transactions.length === 0) {
      return bundle;
    }
    return {
      entrypoint: bootstrapBundle.entrypoint,
      chainId: bundle.chainId,
      intent: bundle.intent,
      transactions: [...bootstrapBundle.transactions, {
        to: bundle.entrypoint,
        data: commons.transaction.encodeBundleExecData(bundle),
        gasLimit: 0,
        delegateCall: false,
        revertOnError: true,
        value: 0
      }]
    };
  }
  decorateSignature(signature, status) {
    if (!status.presignedConfigurations || status.presignedConfigurations.length === 0) {
      return new Promise(resolve => resolve(signature));
    }
    const coder = this.coders.signature;
    const chain = status.presignedConfigurations.map(c => c.signature);
    const chainedSignature = coder.chainSignatures(signature, chain);
    return coder.trim(chainedSignature);
  }
  async publishWitness() {
    const digest = ethers.utils.keccak256(ethers.utils.toUtf8Bytes(`This is a Sequence account woo! ${Date.now()}`));
    const signature = await this.signDigest(digest, 0, false);
    const decoded = this.coders.signature.decode(signature);
    const signatures = this.coders.signature.signaturesOfDecoded(decoded);
    return this.tracker.saveWitnesses({
      wallet: this.address,
      digest,
      chainId: 0,
      signatures
    });
  }
  async signDigest(digest, chainId, decorate = true, cantValidateBehavior = 'ignore') {
    // If we are signing a digest for chainId zero then we can never be fully migrated
    // because Sequence v1 doesn't allow for signing a message on "all chains"

    // So we ignore the state on "chain zero" and instead use one of the states of the networks
    // wallet-webapp should ensure the wallet is as migrated as possible, trying to mimic
    // the behaviour of being migrated on all chains

    const chainRef = ethers.constants.Zero.eq(chainId) ? this.networks[0].chainId : chainId;
    const status = await this.status(chainRef);
    this.mustBeFullyMigrated(status);

    // Check if we can validate onchain and what to do if we can't
    // revert early, since there is no point in signing a digest now
    if (!status.canOnchainValidate && cantValidateBehavior === 'throw') {
      throw new Error('Wallet cannot validate onchain');
    }
    const wallet = this.walletForStatus(chainId, status);
    const signature = await wallet.signDigest(digest);
    const decorated = decorate ? this.decorateSignature(signature, status) : signature;

    // If the wallet can't validate onchain then we
    // need to prefix the decorated signature with all deployments and migrations
    // aka doing a bootstrap using EIP-6492
    if (!status.canOnchainValidate) {
      switch (cantValidateBehavior) {
        // NOTICE: We covered this case before signing the digest
        // case 'throw':
        //   throw new Error('Wallet cannot validate on-chain')
        case 'ignore':
          return decorated;
        case 'eip6492':
          return this.buildEIP6492Signature(await decorated, status, chainId);
      }
    }
    return decorated;
  }
  buildEIP6492Signature(signature, status, chainId) {
    const bootstrapBundle = this.buildBootstrapTransactions(status, chainId);
    if (bootstrapBundle.transactions.length === 0) {
      throw new Error('Cannot build EIP-6492 signature without bootstrap transactions');
    }
    const encoded = ethers.utils.defaultAbiCoder.encode(['address', 'bytes', 'bytes'], [bootstrapBundle.entrypoint, commons.transaction.encodeBundleExecData(bootstrapBundle), signature]);
    return ethers.utils.solidityPack(['bytes', 'bytes32'], [encoded, commons.EIP6492.EIP_6492_SUFFIX]);
  }
  async editConfig(changes) {
    const currentConfig = await this.status(0).then(s => s.config);
    const newConfig = this.coders.config.editConfig(currentConfig, _extends({}, changes, {
      checkpoint: this.coders.config.checkpointOf(currentConfig).add(1)
    }));
    return this.updateConfig(newConfig);
  }
  async updateConfig(config) {
    // config should be for the current version of the wallet
    if (!this.coders.config.isWalletConfig(config)) {
      throw new Error(`Invalid config for wallet ${this.address}`);
    }
    const nextImageHash = this.coders.config.imageHashOf(config);

    // sign an update config struct
    const updateStruct = this.coders.signature.hashSetImageHash(nextImageHash);

    // sign the update struct, using chain id 0
    const signature = await this.signDigest(updateStruct, 0, false);

    // save the presigned transaction to the sessions tracker
    await this.tracker.savePresignedConfiguration({
      wallet: this.address,
      nextConfig: config,
      signature
    });

    // safety check, tracker should have a reverse lookup for the imageHash
    // outside of the local cache
    const reverseConfig = await this.tracker.configOfImageHash({
      imageHash: nextImageHash,
      noCache: true
    });
    if (!reverseConfig || this.coders.config.imageHashOf(reverseConfig) !== nextImageHash) {
      throw Error(`Reverse lookup failed for imageHash ${nextImageHash}`);
    }
  }

  /**
   *  This method is used to bootstrap the wallet on a given chain.
   *  this deploys the wallets and executes all the necessary transactions
   *  for that wallet to start working with the given version.
   *
   *  This usually involves: (a) deploying the wallet, (b) executing migrations
   *
   *  Notice: It should NOT explicitly include chained signatures. Unless internally used
   *  by any of the migrations.
   *
   */
  buildBootstrapTransactions(status, chainId) {
    const transactions = [];

    // Add wallet deployment if needed
    if (!status.onChain.deployed) {
      // Wallet deployment will vary depending on the version
      // so we need to use the context to get the correct deployment
      const deployTransaction = Wallet.buildDeployTransaction(status.original.context, status.original.imageHash);
      transactions.push(...deployTransaction.transactions);
    }

    // Get pending migrations
    transactions.push(...status.signedMigrations.map(m => ({
      to: m.tx.entrypoint,
      data: commons.transaction.encodeBundleExecData(m.tx),
      value: 0,
      gasLimit: 0,
      revertOnError: true,
      delegateCall: false
    })));

    // Build the transaction intent, if the transaction has migrations
    // then we should use one of the intents of the migrations (anyone will do)
    // if it doesn't, then the only intent we could use if the GuestModule one
    // ... but this may fail if the relayer uses a different GuestModule
    const id = status.signedMigrations.length > 0 ? status.signedMigrations[0].tx.intent.id : commons.transaction.subdigestOfGuestModuleTransactions(this.contexts[this.version].guestModule, chainId, transactions);

    // Everything is encoded as a bundle
    // using the GuestModule of the account version
    const {
      guestModule
    } = this.contextFor(status.version);
    return {
      entrypoint: guestModule,
      transactions,
      chainId,
      intent: {
        id,
        wallet: this.address
      }
    };
  }
  async bootstrapTransactions(chainId, prestatus) {
    const status = prestatus || (await this.status(chainId));
    return this.buildBootstrapTransactions(status, chainId);
  }
  async doBootstrap(chainId, feeQuote, prestatus) {
    const bootstrapTxs = await this.bootstrapTransactions(chainId, prestatus);
    return this.relayer(chainId).relay(_extends({}, bootstrapTxs, {
      chainId
    }), feeQuote);
  }
  signMessage(message, chainId, cantValidateBehavior = 'ignore') {
    return this.signDigest(ethers.utils.keccak256(message), chainId, true, cantValidateBehavior);
  }
  async signTransactions(txs, chainId, pstatus) {
    const status = pstatus || (await this.status(chainId));
    this.mustBeFullyMigrated(status);
    const wallet = this.walletForStatus(chainId, status);
    const signed = await wallet.signTransactions(txs);
    return _extends({}, signed, {
      signature: await this.decorateSignature(signed.signature, status)
    });
  }
  async signMigrations(chainId, editConfig) {
    const status = await this.status(chainId);
    if (status.fullyMigrated) return false;
    const wallet = this.walletForStatus(chainId, status);
    const nextConfig = editConfig(wallet.config);
    const signed = await this.migrator.signNextMigration(this.address, status.version, wallet, nextConfig);
    if (!signed) return false;

    // Make sure the tracker has a copy of the config
    // before attempting to save the migration
    // otherwise if this second step fails the tracker could end up
    // with a migration to an unknown config
    await this.tracker.saveWalletConfig({
      config: nextConfig
    });
    const nextCoder = universal.coderFor(nextConfig.version).config;
    const nextImageHash = nextCoder.imageHashOf(nextConfig);
    const reverseConfig = await this.tracker.configOfImageHash({
      imageHash: nextImageHash,
      noCache: true
    });
    if (!reverseConfig || nextCoder.imageHashOf(reverseConfig) !== nextImageHash) {
      throw Error(`Reverse lookup failed for imageHash ${nextImageHash}`);
    }
    await this.tracker.saveMigration(this.address, signed, this.contexts);
    return true;
  }
  async signAllMigrations(editConfig) {
    var _this2 = this;
    const failedChains = [];
    const signedMigrations = await Promise.all(this.networks.map(async function (n) {
      try {
        // Signing migrations for each chain
        return await _this2.signMigrations(n.chainId, editConfig);
      } catch (error) {
        console.warn(`Failed to sign migrations for chain ${n.chainId}`, error);

        // Adding failed chainId to the failedChains array
        failedChains.push(n.chainId);
        // Using null as a placeholder for failed chains
        return null;
      }
    }));

    // Filter out null values to get only the successful signed migrations
    const successfulSignedMigrations = signedMigrations.filter(migration => migration !== null);
    return {
      signedMigrations: successfulSignedMigrations,
      failedChains
    };
  }
  async isMigratedAllChains() {
    var _this3 = this;
    const failedChains = [];
    const statuses = await Promise.all(this.networks.map(async function (n) {
      try {
        return await _this3.status(n.chainId);
      } catch (error) {
        failedChains.push(n.chainId);
        console.warn(`Failed to get status for chain ${n.chainId}`, error);

        // default to true for failed chains
        return {
          fullyMigrated: true
        };
      }
    }));
    const migratedAllChains = statuses.every(s => s.fullyMigrated);
    return {
      migratedAllChains,
      failedChains
    };
  }
  async sendSignedTransactions(signedBundle, chainId, quote, pstatus) {
    const status = pstatus || (await this.status(signedBundle.chainId));
    this.mustBeFullyMigrated(status);
    const decoratedBundle = this.decorateTransactions(signedBundle, status);
    return this.relayer(chainId).relay(decoratedBundle, quote);
  }
  async fillGasLimits(txs, chainId, status) {
    const wallet = this.walletForStatus(chainId, status || (await this.status(chainId)));
    return wallet.fillGasLimits(txs);
  }
  async gasRefundQuotes(txs, chainId, stubSignatureOverrides, status) {
    const wstatus = status || (await this.status(chainId));
    const wallet = this.walletForStatus(chainId, wstatus);
    const predecorated = await this.predecorateTransactions(txs, wstatus, chainId);
    const transactions = commons.transaction.fromTransactionish(this.address, predecorated);

    // We can't sign the transactions (because we don't want to bother the user)
    // so we use the latest configuration to build a "stub" signature, the relayer
    // knows to ignore the wallet signatures
    const stubSignature = wallet.coders.config.buildStubSignature(wallet.config, stubSignatureOverrides);

    // Now we can decorate the transactions as always, but we need to manually build the signed bundle
    const intentId = ethers.utils.hexlify(ethers.utils.randomBytes(32));
    const signedBundle = {
      chainId,
      intent: {
        id: intentId,
        wallet: this.address
      },
      signature: stubSignature,
      transactions,
      entrypoint: this.address,
      nonce: 0 // The relayer also ignored the nonce
    };

    const decoratedBundle = this.decorateTransactions(signedBundle, wstatus);
    const data = commons.transaction.encodeBundleExecData(decoratedBundle);
    const res = await this.relayer(chainId).getFeeOptionsRaw(decoratedBundle.entrypoint, data);
    return _extends({}, res, {
      decorated: decoratedBundle
    });
  }
  async prepareTransactions(args) {
    const status = await this.status(args.chainId);
    const transactions = await this.fillGasLimits(args.txs, args.chainId, status);
    const gasRefundQuote = await this.gasRefundQuotes(transactions, args.chainId, args.stubSignatureOverrides, status);
    const flatDecorated = commons.transaction.unwind(this.address, gasRefundQuote.decorated.transactions);
    return {
      transactions,
      flatDecorated,
      feeOptions: gasRefundQuote.options,
      feeQuote: gasRefundQuote.quote
    };
  }
  async sendTransaction(txs, chainId, quote, skipPreDecorate = false, callback) {
    const status = await this.status(chainId);
    const predecorated = skipPreDecorate ? txs : await this.predecorateTransactions(txs, status, chainId);
    const signed = await this.signTransactions(predecorated, chainId);
    if (callback) callback(signed);
    return this.sendSignedTransactions(signed, chainId, quote);
  }
  async signTypedData(domain, types, message, chainId, cantValidateBehavior = 'ignore') {
    const digest = encodeTypedDataDigest({
      domain,
      types,
      message
    });
    return this.signDigest(digest, chainId, true, cantValidateBehavior);
  }
  async getAllSigners() {
    var _this4 = this;
    const allSigners = [];

    // We need to get the signers for each status
    await Promise.all(this.networks.map(async function (network) {
      const chainId = network.chainId;

      // Getting the status with `longestPath` set to true will give us all the possible configurations
      // between the current onChain config and the latest config, including the ones "flagged for removal"
      const status = await _this4.status(chainId, true);
      const fullChain = [status.onChain.imageHash, ...(status.onChain.version !== status.version ? status.signedMigrations.map(m => universal.coderFor(m.toVersion).config.imageHashOf(m.toConfig)) : []), ...status.presignedConfigurations.map(update => update.nextImageHash)];
      return Promise.all(fullChain.map(async function (nextImageHash, iconf) {
        const isLast = iconf === fullChain.length - 1;
        const config = await _this4.tracker.configOfImageHash({
          imageHash: nextImageHash
        });
        if (!config) {
          console.warn(`AllSigners may be incomplete, config not found for imageHash ${nextImageHash}`);
          return;
        }
        const coder = universal.genericCoderFor(config.version);
        const signers = coder.config.signersOf(config);
        signers.forEach(signer => {
          const exists = allSigners.find(s => s.address === signer.address && s.network === chainId);
          if (exists && isLast && exists.flaggedForRemoval) {
            exists.flaggedForRemoval = false;
            return;
          }
          if (exists) return;
          allSigners.push({
            address: signer.address,
            weight: signer.weight,
            network: chainId,
            flaggedForRemoval: !isLast
          });
        });
      }));
    }));
    return allSigners;
  }
}
function isAccount(value) {
  return value instanceof Account;
}

export { Account, isAccount };
